import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Link, TextField, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button'

const authenticatorSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Code must be 6 digits'),
})

const recoverySchema = z.object({
  // Codes are handed out as xxxxx-xxxxx; the server normalises case and separators.
  code: z
    .string()
    .trim()
    .regex(/^[a-z0-9]{5}-?[a-z0-9]{5}$/i, 'Enter a recovery code in the format xxxxx-xxxxx'),
})

type FormData = z.infer<typeof authenticatorSchema>

type VerifyCodeVariant = 'authenticator' | 'recovery'

interface VariantConfig {
  schema: z.ZodType<FormData>
  introText: string
  placeholder: string
  maxLength: number
  inputMode?: 'numeric'
  submitLabel: string
  backLabel: string
}

const VARIANT_CONFIG: Record<VerifyCodeVariant, VariantConfig> = {
  authenticator: {
    schema: authenticatorSchema,
    introText: 'Enter the 6-digit code your authenticator app shows for PayDeck.',
    placeholder: 'Enter code',
    maxLength: 6,
    inputMode: 'numeric',
    submitLabel: 'Verify Code',
    backLabel: 'Back to the QR code',
  },
  recovery: {
    schema: recoverySchema,
    introText:
      'Enter one of the recovery codes you saved when you set up your authenticator app. Each code works once.',
    placeholder: 'xxxxx-xxxxx',
    maxLength: 11,
    submitLabel: 'Sign In',
    backLabel: 'Back to verification code',
  },
}

interface Props {
  variant: VerifyCodeVariant
  isPending: boolean
  // Server message for a rejected code (already used, incorrect, locked).
  errorMessage?: string
  onVerify: (code: string) => void
  onBack?: VoidFunction
}

export function VerifyCodeForm({ variant, isPending, errorMessage, onVerify, onBack }: Props) {
  const config = VARIANT_CONFIG[variant]

  const form = useForm<FormData>({
    resolver: zodResolver(config.schema),
    defaultValues: { code: '' },
  })

  const submit = ({ code }: FormData) => onVerify(code)

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      form.handleSubmit(submit)()
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2">{config.introText}</Typography>
      <Controller
        name="code"
        control={form.control}
        render={({ field }) => (
          <TextField
            {...field}
            placeholder={config.placeholder}
            inputProps={{ inputMode: config.inputMode, autoComplete: 'one-time-code', maxLength: config.maxLength }}
            error={!!form.formState.errors.code || !!errorMessage}
            helperText={form.formState.errors.code?.message ?? errorMessage}
            autoFocus
            onKeyDown={handleKeyDown}
          />
        )}
      />
      <Button
        loading={isPending}
        color="primary"
        variant="contained"
        size="large"
        fullWidth
        onClick={form.handleSubmit(submit)}
      >
        {config.submitLabel}
      </Button>
      {onBack && (
        <Link component="button" type="button" typography="body2" color="secondary.dark" onClick={onBack}>
          {config.backLabel}
        </Link>
      )}
    </Box>
  )
}
